import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, SmallTitleSpan } from "src/components/common/common";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, postSortHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useKSK1030_R01Store } from "./Store_KSK1030_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { KSK1030_R01LotDto } from "./Dto_KSK1030_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

const COLUMN_STATE_KEY = 'KSK1030_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<KSK1030_R01LotDto> | null>;
	defaultKSK1030_R01LotRow: KSK1030_R01LotDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const LotTable_KSK1030_R01 = (props: Props) => {
	const { lotGridRef, defaultKSK1030_R01LotRow, clickable, setClickable } = props;

	const {
		KSK1030_R01LotMap,
		setKSK1030_R01LotList,
		updateKSK1030_R01LotList,
		selectedKSK1030_R01LotList, addSelectedKSK1030_R01LotList, removeSelectedKSK1030_R01LotList,
		initSelectedKSK1030_R01LotList,
		deletedKSK1030_R01LotList,
		originalKSK1030_R01LotList,
		selectedItemCdForLot,
		lotColumnState, setLotColumnState,
		editKSK1030_R01,
	} = useKSK1030_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const gridData = useMemo(
		() => KSK1030_R01LotMap.get(selectedItemCdForLot || '') || [],
		[KSK1030_R01LotMap, selectedItemCdForLot],
	);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData,
		defaultGridData: defaultKSK1030_R01LotRow,
		selectedRow: selectedKSK1030_R01LotList,
		originalGridData: originalKSK1030_R01LotList,
		updateRow: (index: number, data: any) => updateKSK1030_R01LotList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		lotNo: 'LOT No',
		lotQty: '재고량',
		inputOutQty: '출고량',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['lotNo', 'lotQty'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['inputOutQty'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK1030_R01LotDto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK1030_R01LotList,
			deletedList: deletedKSK1030_R01LotList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedKSK1030_R01LotList, deletedKSK1030_R01LotList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const commonInputColumns = ['inputOutQty'];
	const defaultEditableColumns = ['inputOutQty'];

	const baseColumnDefs: ColDef<KSK1030_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 85,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '출고량', colId: 'inputOutQty', field: 'inputOutQty', width: 85,
			editable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 170,
			editable: false,
		},
	], []);

	const columnDefs: ColDef<KSK1030_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK1030_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	const handleCellChange = useCellChangeHandler({
		dataList: gridData,
		originalDataList: originalKSK1030_R01LotList,
		updateRow: updateKSK1030_R01LotList,
		customBeforeUpdate: (updatedData) => updatedData,
		customAfterUpdate: (updatedData) => updatedData,
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: gridData,
		idKey: 'valueIndex',
		selectedList: selectedKSK1030_R01LotList,
		deletedList: deletedKSK1030_R01LotList,
		addSelected: addSelectedKSK1030_R01LotList,
		removeSelected: removeSelectedKSK1030_R01LotList,
		clickable, setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: gridData as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		},
	});

	const cellClickActions: CustomCellClickAction<KSK1030_R01LotDto>[] = useMemo(() => [
		{
			validate: (field) => commonInputColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], []);
	const handleCellClick = CreateCellClickHandler(gridData, 'valueIndex', cellClickActions);

	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK1030_R01LotDto>[] = useMemo(() => [], [editKSK1030_R01?.warehouseCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(gridData, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<KSK1030_R01LotDto>) => {
		postSortHandler(params, gridData, originalKSK1030_R01LotList, setKSK1030_R01LotList);
	}, [gridData, originalKSK1030_R01LotList, setKSK1030_R01LotList]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			lotNo: gridData.length.toLocaleString() + "건",
			inputOutQty: gridData.reduce((sum, item) => sum + (deletedKSK1030_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.inputOutQty) || 0), 0),
			lotQty: gridData.reduce((sum, item) => sum + (deletedKSK1030_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];
	}, [gridData, deletedKSK1030_R01LotList]);

	return (
		<div className="flex flex-1 gap-[4px] h-full min-h-full">
			<GridContextWrapper<KSK1030_R01LotDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={lotGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK1030_R01LotList}
				initSelected={initSelectedKSK1030_R01LotList}
				defaultRowData={defaultKSK1030_R01LotRow}
				baseColumnDefs={columnDefs}
				setColumnState={setLotColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<div className="w-full h-[32px] shrink-0 [&>span]:!h-[32px] [&>span]:!min-h-[32px] [&>span]:!max-h-[32px] [&>span]:!py-0 [&>span]:box-border">
					<SmallTitleSpan>Lot 출고 입력</SmallTitleSpan>
				</div>
				<Grid_Excel<KSK1030_R01LotDto>
					ref={lotGridRef}
					rowData={paddedGridData} getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					rowSelection="multiple"
					rowClassRules={createRowClassRules<KSK1030_R01LotDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK1030_R01LotList,
						deletedList: deletedKSK1030_R01LotList,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					onPostSortRows={handlePostSort}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setLotColumnState}
				/>
			</GridContextWrapper>
		</div>
	);
};
